import {Button} from '@wandb/components';
import {Link} from '@wandb/weave/common/util/links';
import {Select} from '@wandb/weave/components/Form/Select';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useContext, useEffect} from 'react';
import {useShallow} from 'zustand/react/shallow';

import {envIsPublicCloud} from '../../../../config';
import {createDismissibleBanner} from '../../../../pages/Billing/AccountSettings/WarningBanner/DismissibleBanner';
import {DEFAULT_ONPREM_ORGANIZATION_NAME} from '../../../../pages/OrgDashboard/OrgRoles/RolesDashboard';
import {useTeamMember} from '../../../../state/graphql/teamMembers';
import {useViewer} from '../../../../state/viewer/hooks';
import {useAdminModeActive} from '../../../../util/admin';
import DOC_URLS from '../../../../util/doc_urls';
import * as urls from '../../../../util/urls';
import {BannerFlexWrapper, BannerTextWrapper} from '../../../Banner';
import {AccountSelectorContext} from '../../../Search/SearchNav/AccountSelectorContextProvider';
import {type GenericWebhook} from '../../../Webhooks/types';
import {CodeEditor} from '../../CodeEditor';
import {ConfigKey} from '../ConfigKey';
import {useTriggerTemplateVariables} from '../hooks/useTriggerTemplateVariables';
import {useActionConfigStore} from './ActionStore';
import * as S from './CreateTriggerDrawerLoaded.styles';
import {useEventStore} from './EventStore';

const extractTemplateKey = (templateString: string): string => {
  return templateString.replace('${', '').replace('}', '');
};

const formatWebhookOptionLabel = (webhookOption: WebhookOptionType | null) => {
  if (webhookOption == null) {
    return;
  }
  const {label} = webhookOption;
  return (
    <S.SelectOption>
      <Icon name="webhook" />
      <S.SelectOptionLabelText title={label}>{label}</S.SelectOptionLabelText>
    </S.SelectOption>
  );
};

type WebhookOptionType = {
  label: string;
  value: GenericWebhook;
};
type WebhookConfigurationProps = {
  webhooks: GenericWebhook[];
};

type PayloadInfoBannerProps = {
  userID: string;
  orgName: string;
};

const PayloadInfoBanner = createDismissibleBanner<PayloadInfoBannerProps>({
  id: ({userID, orgName}) => `webhook-payload-info:${orgName}:${userID}`,
  Component: ({onDismiss}) => {
    return (
      <Tailwind>
        <S.BannerWithMargin variant="warning">
          <BannerFlexWrapper>
            <Icon name="info" />
            <BannerTextWrapper>
              <span>
                Visit{' '}
                <Link
                  style={{textDecoration: 'underline'}}
                  to={`${DOC_URLS.automationCreateWebhook}#troubleshoot-your-webhook`}>
                  documentation
                </Link>{' '}
                ("Bash script" tab) to preview the structure of the POST request
                we send, including where contents are inserted.
              </span>
            </BannerTextWrapper>
            <button
              className="flex items-center border-none bg-transparent font-semibold text-sienna-600 hover:cursor-pointer hover:text-sienna-500"
              onClick={onDismiss}>
              <Icon name={'close'} />
            </button>
          </BannerFlexWrapper>
        </S.BannerWithMargin>
      </Tailwind>
    );
  },
});

export const WebhookConfiguration: React.FC<WebhookConfigurationProps> = ({
  webhooks,
}) => {
  const eventConfig = useEventStore(useShallow(s => s));
  const templateStrings = useTriggerTemplateVariables(eventConfig.eventType);
  const viewer = useViewer();
  const username = viewer?.username;
  const {selectedAccount} = useContext(AccountSelectorContext);
  const {updateWebhookConfig, entityName} = useActionConfigStore(
    useShallow(s => s)
  );
  const {webhook, webhookPayload, webhookPayloadError} = useActionConfigStore(
    useShallow(s => s.webhook)
  );

  const onWebhookPayloadChange = (value: string) => {
    updateWebhookConfig({webhookPayload: value, webhookPayloadError: ''});
  };

  const orgName = envIsPublicCloud
    ? (selectedAccount?.name ?? '')
    : DEFAULT_ONPREM_ORGANIZATION_NAME;

  const viewerIsAdmin = useAdminModeActive();
  const {viewerIsTeamAdmin} = useTeamMember({
    viewer,
    entityName: entityName,
  });

  const webhookOptions: WebhookOptionType[] = webhooks.map(w => {
    return {
      value: w,
      label: w.name,
    };
  });
  const defaultWebhookOption = webhookOptions.find(
    w => w.value.id === webhook?.id
  );

  // Validate json payload
  useEffect(() => {
    try {
      JSON.parse(webhookPayload);
      updateWebhookConfig({webhookPayloadError: ''});
    } catch (e) {
      updateWebhookConfig({
        webhookPayloadError: 'Payload must be valid JSON',
      });
    }
  }, [updateWebhookConfig, webhookPayload]);

  const handleAddTemplateStrings = (templateStringsToAdd: string[]) => {
    try {
      const currentRecords = JSON.parse(webhookPayload);
      if (currentRecords === null) {
        updateWebhookConfig({
          webhookPayloadError:
            'Payload must be valid JSON to apply new template string',
        });
        return;
      }
      for (const templateString of templateStringsToAdd) {
        const jsonKey = extractTemplateKey(templateString);
        if (currentRecords[jsonKey]) {
          continue;
        }
        currentRecords[jsonKey] = templateString;
      }
      onWebhookPayloadChange(JSON.stringify(currentRecords, null, 2));
    } catch (e) {
      updateWebhookConfig({
        webhookPayloadError:
          'Payload must be valid JSON to add parameters to payload',
      });
    }
  };

  return (
    <>
      <Tailwind>
        <S.BannerWithMargin variant="info">
          <BannerFlexWrapper>
            <Icon name="lightbulb-info" />
            <BannerTextWrapper>
              Webhooks can be managed and tested in your team settings by team
              admins.
            </BannerTextWrapper>
            {
              // Only show the team settings link to admins, since they're the only ones who can make changes.
              (viewerIsTeamAdmin || viewerIsAdmin) && (
                <button className="flex items-center whitespace-nowrap border-none bg-transparent font-semibold text-moon-800 dark:text-wb-text-primary hover:cursor-pointer hover:text-moon-950 dark:hover:text-wb-text-primary">
                  <Link to={urls.teamSettings(entityName ?? '')}>
                    Team settings
                  </Link>
                </button>
              )
            }
          </BannerFlexWrapper>
        </S.BannerWithMargin>
      </Tailwind>
      <S.LabelRow>
        <S.Label>Webhook</S.Label>
      </S.LabelRow>
      <Select<WebhookOptionType | null>
        options={webhookOptions}
        placeholder="Select a webhook..."
        defaultValue={defaultWebhookOption}
        onChange={(d: WebhookOptionType | null) => {
          if (d) {
            updateWebhookConfig({webhook: d.value});
          }
        }}
        autoFocus={true}
        formatOptionLabel={formatWebhookOptionLabel}
      />
      <S.LabelRow>
        <S.Label>Payload</S.Label>
      </S.LabelRow>
      <CodeEditor
        value={webhookPayload}
        language="json"
        onChange={onWebhookPayloadChange}
      />
      {webhookPayloadError && <S.Error>{webhookPayloadError}</S.Error>}
      <PayloadInfoBanner orgName={orgName} userID={username ?? ''} />
      <S.PlainText>
        Use the following keys to refer to condition objects in the payload
        parameters. See{' '}
        <Link to={`${DOC_URLS.automationCreateWebhook}#payload-examples`}>
          example webhook payloads
        </Link>{' '}
        for common use cases: Github Actions, Slack and Teams notifications.
      </S.PlainText>
      <Tailwind>
        <div className="flex w-full items-center justify-end">
          <Button
            onClick={() =>
              handleAddTemplateStrings(templateStrings.map(ts => ts.value))
            }>
            + Add All Parameters
          </Button>
        </div>
      </Tailwind>
      {templateStrings.map(ts => (
        <S.TemplateHelperRow key={ts.value}>
          <S.TemplateHelperKey key={`key-${ts.value}`}>
            <ConfigKey
              configKey={ts.value}
              onPlusClick={() => handleAddTemplateStrings([ts.value])}
            />
          </S.TemplateHelperKey>
          <S.TemplateHelperString key={ts.value}>
            {ts.help}
          </S.TemplateHelperString>
        </S.TemplateHelperRow>
      ))}
    </>
  );
};
